import { z } from "zod";
import { IdSchema, IsoDateTimeSchema } from "./common.js";

/** Exact phrase required by the destructive database initialization command. */
export const DB_INITIALIZATION_CONFIRMATION_PHRASE = "INITIALIZE ZEROVIA DATABASE" as const;

export const DatabaseInitializationRequestSchema = z.object({
	confirmationPhrase: z.literal(DB_INITIALIZATION_CONFIRMATION_PHRASE),
});
export type DatabaseInitializationRequest = z.infer<typeof DatabaseInitializationRequestSchema>;

export const DatabaseBackupFileSchema = z.object({
	name: z.string().min(1),
	tableCount: z.number().int().min(0),
	rowCount: z.number().int().min(0),
});

export const DatabaseBackupSchema = z.object({
	id: IdSchema,
	createdAt: IsoDateTimeSchema,
	path: z.string().min(1),
	verified: z.boolean(),
	failure: z.string().nullable(),
	databases: z.array(DatabaseBackupFileSchema),
	storageObjectRowCount: z.number().int().min(0),
	objectStoreFileCount: z.number().int().min(0),
});
export type DatabaseBackup = z.infer<typeof DatabaseBackupSchema>;

export const DatabaseBackupListSchema = z.object({
	items: z.array(DatabaseBackupSchema),
});
export type DatabaseBackupList = z.infer<typeof DatabaseBackupListSchema>;

export const DatabaseInitializationStageSchema = z.enum([
	"BACKUP",
	"BACKUP_VERIFIED",
	"DELETE",
	"SEED",
	"VERIFY",
]);
export type DatabaseInitializationStage = z.infer<typeof DatabaseInitializationStageSchema>;

export const DatabaseInitializationResultSchema = z.object({
	operationId: IdSchema,
	status: z.literal("SUCCEEDED"),
	backup: DatabaseBackupSchema,
	deletedCounts: z.record(z.string(), z.number().int().min(0)),
	completedStages: z.array(DatabaseInitializationStageSchema),
	durationMs: z.number().int().min(0),
});
export type DatabaseInitializationResult = z.infer<typeof DatabaseInitializationResultSchema>;

/** Internal Block command. It is shared for response validation, not exposed as an external route. */
export const DatabaseMaintenanceRequestSchema = z.object({
	phase: z.enum(["DELETE", "SEED", "VERIFY"]),
	operationId: IdSchema,
});
export type DatabaseMaintenanceRequest = z.infer<typeof DatabaseMaintenanceRequestSchema>;

export const DatabaseMaintenanceResultSchema = z.object({
	service: z.string().min(1),
	phase: z.enum(["DELETE", "SEED", "VERIFY"]),
	counts: z.record(z.string(), z.number().int().min(0)),
});
export type DatabaseMaintenanceResult = z.infer<typeof DatabaseMaintenanceResultSchema>;

/** Exact phrase required by the destructive data import command. */
export const DATA_IMPORT_CONFIRMATION_PHRASE = "IMPORT ZEROVIA DATA PACKAGE" as const;

export const DataExportPackageStatusSchema = z.enum(["RUNNING", "SUCCEEDED", "FAILED"]);
export type DataExportPackageStatus = z.infer<typeof DataExportPackageStatusSchema>;

export const DataExportPackageFileSchema = z.object({
	path: z.string().min(1),
	kind: z.enum(["sqlite", "object-store", "credential-vault", "key", "manifest", "other"]),
	bytes: z.number().int().min(0).optional(),
	tableCount: z.number().int().min(0).optional(),
	rowCount: z.number().int().min(0).optional(),
});
export type DataExportPackageFile = z.infer<typeof DataExportPackageFileSchema>;

export const DataExportPackageSchema = z.object({
	id: IdSchema,
	createdAt: IsoDateTimeSchema,
	contractVersion: z.string().min(1),
	/** RUNNING: server-side job in progress (client may disconnect). */
	status: DataExportPackageStatusSchema,
	verified: z.boolean(),
	failure: z.string().nullable(),
	archivePath: z.string().min(1),
	archiveBytes: z.number().int().min(0),
	files: z.array(DataExportPackageFileSchema),
	databases: z.array(DatabaseBackupFileSchema),
	objectStoreFileCount: z.number().int().min(0),
	storageObjectRowCount: z.number().int().min(0),
	includesAuthKey: z.boolean(),
	includesCredentialVault: z.boolean(),
});
export type DataExportPackage = z.infer<typeof DataExportPackageSchema>;

export const DataExportPackageListSchema = z.object({
	items: z.array(DataExportPackageSchema),
});
export type DataExportPackageList = z.infer<typeof DataExportPackageListSchema>;

export const DataImportRequestSchema = z.object({
	confirmationPhrase: z.literal(DATA_IMPORT_CONFIRMATION_PHRASE),
});
export type DataImportRequest = z.infer<typeof DataImportRequestSchema>;

export const DataImportResultSchema = z.object({
	operationId: IdSchema,
	status: z.literal("SUCCEEDED"),
	importedPackageId: IdSchema,
	safetyBackupId: IdSchema,
	requiresRestart: z.literal(true),
	durationMs: z.number().int().min(0),
	databases: z.array(DatabaseBackupFileSchema),
	objectStoreFileCount: z.number().int().min(0),
});
export type DataImportResult = z.infer<typeof DataImportResultSchema>;

/** Exact phrase required to restart the Control Plane process/machine. */
export const CONTROL_PLANE_RESTART_CONFIRMATION_PHRASE = "RESTART ZEROVIA CONTROL PLANE" as const;

export const ControlPlaneRestartRequestSchema = z.object({
	confirmationPhrase: z.literal(CONTROL_PLANE_RESTART_CONFIRMATION_PHRASE),
});
export type ControlPlaneRestartRequest = z.infer<typeof ControlPlaneRestartRequestSchema>;

export const ControlPlaneRestartResultSchema = z.object({
	operationId: IdSchema,
	status: z.literal("ACCEPTED"),
	method: z.enum(["fly-machines", "deferred-exit"]),
	appName: z.string().min(1).nullable(),
	machineId: z.string().min(1).nullable(),
	message: z.string().min(1),
});
export type ControlPlaneRestartResult = z.infer<typeof ControlPlaneRestartResultSchema>;

/** Exact phrase required to purge reclaimable backups/exports (not live SQLite). */
export const STORAGE_PURGE_CONFIRMATION_PHRASE = "PURGE ZEROVIA RECLAIMABLE STORAGE" as const;

export const StorageBucketUsageSchema = z.object({
	bytes: z.number().int().min(0),
	entryCount: z.number().int().min(0),
});
export type StorageBucketUsage = z.infer<typeof StorageBucketUsageSchema>;

export const StorageArtifactKindSchema = z.enum([
	"db-backup",
	"export",
	"safety",
	"orphan",
]);
export type StorageArtifactKind = z.infer<typeof StorageArtifactKindSchema>;

export const StorageArtifactSchema = z.object({
	id: IdSchema,
	kind: StorageArtifactKindSchema,
	/** Basename under backups/ or exports/ (directory and/or archive stem). */
	name: z.string().min(1),
	path: z.string().min(1),
	bytes: z.number().int().min(0),
	createdAt: IsoDateTimeSchema.nullable(),
	deletable: z.literal(true),
	/** True when an in-progress export job owns this artifact. */
	blockedReason: z.string().min(1).nullable(),
});
export type StorageArtifact = z.infer<typeof StorageArtifactSchema>;

export const StorageUsageSchema = z.object({
	dataDir: z.string().min(1),
	totalBytes: z.number().int().min(0).nullable(),
	usedBytes: z.number().int().min(0).nullable(),
	availableBytes: z.number().int().min(0).nullable(),
	breakdown: z.object({
		backups: StorageBucketUsageSchema,
		exports: StorageBucketUsageSchema,
		liveSqlite: StorageBucketUsageSchema,
		objectStore: StorageBucketUsageSchema,
		other: StorageBucketUsageSchema,
	}),
	artifacts: z.array(StorageArtifactSchema),
	checkedAt: IsoDateTimeSchema,
});
export type StorageUsage = z.infer<typeof StorageUsageSchema>;

export const StorageArtifactDeleteResultSchema = z.object({
	operationId: IdSchema,
	status: z.literal("SUCCEEDED"),
	artifact: StorageArtifactSchema,
	freedBytes: z.number().int().min(0),
});
export type StorageArtifactDeleteResult = z.infer<typeof StorageArtifactDeleteResultSchema>;

export const StoragePurgeRequestSchema = z.object({
	confirmationPhrase: z.literal(STORAGE_PURGE_CONFIRMATION_PHRASE),
});
export type StoragePurgeRequest = z.infer<typeof StoragePurgeRequestSchema>;

export const StoragePurgeResultSchema = z.object({
	operationId: IdSchema,
	status: z.literal("SUCCEEDED"),
	deletedCount: z.number().int().min(0),
	skippedCount: z.number().int().min(0),
	freedBytes: z.number().int().min(0),
	deletedIds: z.array(IdSchema),
	skippedIds: z.array(IdSchema),
});
export type StoragePurgeResult = z.infer<typeof StoragePurgeResultSchema>;
